import React from 'react';
import { connect } from 'react-redux';
import { PortalContextProvider, Text } from '@amzn/storm-ui';
import { RootState } from 'src/app/store';
import {
  CommunicationsDrawerInfo,
  selectCommunicationsDrawerInfo,
} from 'src/features/header/store/slice';
import { selectViewType } from 'src/app/slice';
import { CommunicationsDrawerWrapper } from './styles';
import { UtilitiesIconButton } from '../styles';
import { Bell } from './Bell';
import { ErrorBoundary } from 'src/commons/components/ErrorBoundary';
import FederatedModule from './FederatedModule';
import { CommunicationsDrawerPublisher } from 'src/commons/utils/metrics';
import { communicationsDrawerTaktId } from './constants';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { useAdvertisingApiContext } from 'src/providers/AdvertisingApiProvider';

export const DynamicCommunicationsDrawer: React.VFC<{
  communicationsDrawerInfo?: CommunicationsDrawerInfo;
}> = ({ communicationsDrawerInfo }) => {
  const isTacticalCardV2Enabled = useIsFeatureEnabled(
    WeblabedFeatures.BELL_DRAWER_TACTICAL_CARD_V2,
  );
  const { advertisingApiClient, advertisingApiCsrfToken } =
    useAdvertisingApiContext();

  // render the dynamic drawer in one of the two conditions below
  // 1. if tactical card v2 is not enable
  // 2. if tactical card v2 is enabled and csrf token is not empty
  const shouldRenderDynamicBellDrawer =
    !isTacticalCardV2Enabled ||
    (isTacticalCardV2Enabled && advertisingApiCsrfToken !== '');

  const bellEl = React.useRef<HTMLElement>(null);

  const [isCommunicationsDrawerOpen, setIsCommunicationsDrawerOpen] =
    React.useState(false);
  const [showBadge, setShowBadge] = React.useState(false);

  const toggleCommunicationsDrawer = () => {
    CommunicationsDrawerPublisher.publishCounterMonitor(
      isCommunicationsDrawerOpen ? 'Close' : 'Open',
      1,
    );
    setIsCommunicationsDrawerOpen(!isCommunicationsDrawerOpen);
  };

  // todo: refactor this code to only fire when the button is clicked
  // the code below attaches a click listener to the entire dom object
  React.useEffect(() => {
    function onKeyClick({ key }) {
      if (key == 'Escape') {
        toggleCommunicationsDrawer();
      }
    }

    const onClickHandler = ({ target }) => {
      if (target instanceof Node && bellEl && bellEl.current) {
        if (
          bellEl.current === target ||
          bellEl.current.firstChild === target ||
          bellEl.current.firstChild?.contains(target)
        ) {
          //click bell icon should toggle the drawer
          toggleCommunicationsDrawer();
        } else if (
          !bellEl.current.contains(target) &&
          isCommunicationsDrawerOpen
        ) {
          CommunicationsDrawerPublisher.publishCounterMonitor('Close', 1);
          //click anywhere other that bell icon or inside the drawer should close the drawer
          setIsCommunicationsDrawerOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', onClickHandler);

    if (isCommunicationsDrawerOpen) {
      document.addEventListener('keydown', onKeyClick);
      return () => {
        document.removeEventListener('keydown', onKeyClick);
        document.removeEventListener('mousedown', onClickHandler);
      };
    }
    return () => {
      document.removeEventListener('mousedown', onClickHandler);
    };
  }, [isCommunicationsDrawerOpen]);

  if (!communicationsDrawerInfo) {
    return null;
  }

  return (
    <ErrorBoundary name="aces-dynamic-communications-drawer">
      <CommunicationsDrawerWrapper ref={bellEl}>
        <PortalContextProvider>
          <UtilitiesIconButton
            className="communications-drawer-button"
            id={communicationsDrawerTaktId}
            data-takt-id={communicationsDrawerTaktId}
            aria-haspopup="true"
            aria-expanded={isCommunicationsDrawerOpen}
          >
            <Bell showBadge={showBadge} aria-hidden="true" />
            <Text srOnly>{communicationsDrawerInfo.texts.label}</Text>
          </UtilitiesIconButton>
          {shouldRenderDynamicBellDrawer && (
            <FederatedModule
              configs={{
                variant: 'IA2',
                enableCardV2: isTacticalCardV2Enabled,
                advertisingApiClient:
                  advertisingApiClient.getAdvertisingApiClient(),
              }}
              srLabel={communicationsDrawerInfo.texts.label}
              entityId={communicationsDrawerInfo.texts.entityId}
              isOpen={isCommunicationsDrawerOpen}
              onNewNotifications={setShowBadge}
              triggerComponent={bellEl}
            />
          )}
        </PortalContextProvider>
      </CommunicationsDrawerWrapper>
    </ErrorBoundary>
  );
};

const acesConnector = connect((state: RootState) => {
  return {
    communicationsDrawerInfo: selectCommunicationsDrawerInfo(state),
    viewType: selectViewType(state),
  };
});

export const ConnectedDynamicCommunicationsDrawer = acesConnector(
  DynamicCommunicationsDrawer,
);
